import {
  CADPoint,
  CompoundCurve,
  type ICADObject,
  type LineEditingAction,
} from '@technology/web3d-plugin-line-editing';

import { FeatureScope, IFeatureService } from '../interfaces/feature-store';
import { MapLayer } from '../types/map-layer';
import { GeospatialFeature } from '../types/geospatial-feature';
import { FeatureGeometry, Position3D } from '../types/geometry';
import {
  childGeospatialLogger,
  type GeospatialLogger,
} from '../logging/geospatial-logger';
import {
  CAD_FEATURE_ID_KEY,
  CAD_SOURCE_FEATURE_KEY,
  type CachedSourceFeature,
  stampFeatureLink,
} from '../interfaces/cad-object-metadata';
import {
  cadGeometryFromCoordinates,
  cadGeometryKind,
  cadVertices,
  type CadVertex,
} from '../transformer/cad-geometry-reader';

export type LineEditingActionSource = (
  handler: (action: LineEditingAction) => void
) => () => void;

/** Scene X,Y → WGS-84 lon/lat (inverse of the mirror's projector). */
export type SceneToGeographic = (
  x: number,
  y: number
) => { lon: number; lat: number } | undefined;

/**
 * Preferred authoring frame when TGL validation confirms the scene matches the
 * point-cloud CRS; otherwise fall back to {@link SceneToGeographic} (WGS-84).
 */
export interface PointCloudCrsAuthoring {
  toCoordinates(x: number, y: number, z: number): Position3D | undefined;
  readonly crsUrn: string;
}

export interface FeatureEditingControllerOptions {
  getFeatureService(): IFeatureService;
  readonly logger: GeospatialLogger;
  getScope(): FeatureScope | undefined;
  getActiveLayer(): MapLayer | undefined;
  getCrsUrn(): string | undefined;
  /** When present, persist points as WGS-84 so they geolocate like streamed features. */
  getSceneToGeographic?(): SceneToGeographic | undefined;
  /** When validated, prefer point-cloud CRS authoring over WGS-84. */
  getPointCloudCrsAuthoring?(): PointCloudCrsAuthoring | undefined;
  /** Fired after at least one successful write so the host can rotate the tile cache token. */
  onFeaturesChanged?(): void;
  /**
   * Fired after a feature is created or moved, with the persisted feature in
   * WGS-84 (when a converter is available) so the host can optimistically upsert
   * it into shared state.
   */
  onFeaturePersisted?(feature: GeospatialFeature): void;
  /** Fired after features are deleted so the host can drop them from shared state. */
  onFeaturesRemoved?(featureIds: readonly string[]): void;
}

const LAYER_ID_METADATA_KEY = 'common_layerId';
const TEMPLATE_ID_METADATA_KEY = 'collection_templateId';
const TEMPLATE_SERIES_ID_METADATA_KEY = 'collection_templateSeriesId';
const GEOMETRY_TYPE_METADATA_KEY = 'geometry_type';
const DESCRIPTION_PROPERTY_KEY = 'text';
const MIN_LINESTRING_VERTICES = 2;

/**
 * Write-only sync: line-editing actions → {@link IFeatureService} CRUD.
 * Stamps {@link CAD_FEATURE_ID_KEY} / {@link CAD_SOURCE_FEATURE_KEY} for update
 * vs create and optimistic cache.
 */
export class FeatureEditingController {
  private readonly getFeatureService: () => IFeatureService;
  private readonly logger: GeospatialLogger;
  private readonly getScope: () => FeatureScope | undefined;
  private readonly getActiveLayer: () => MapLayer | undefined;
  private readonly getCrsUrn: () => string | undefined;
  private readonly getSceneToGeographic?: () => SceneToGeographic | undefined;
  private readonly getPointCloudCrsAuthoring?: () =>
    | PointCloudCrsAuthoring
    | undefined;
  private readonly onFeaturesChanged?: () => void;
  private readonly onFeaturePersisted?: (feature: GeospatialFeature) => void;
  private readonly onFeaturesRemoved?: (featureIds: readonly string[]) => void;
  private unsubscribe: (() => void) | undefined;
  private generation = 0;

  constructor(options: FeatureEditingControllerOptions) {
    this.getFeatureService = options.getFeatureService;
    this.logger = childGeospatialLogger(
      options.logger,
      'FeatureEditingController'
    );
    this.getScope = options.getScope;
    this.getActiveLayer = options.getActiveLayer;
    this.getCrsUrn = options.getCrsUrn;
    this.getSceneToGeographic = options.getSceneToGeographic;
    this.getPointCloudCrsAuthoring = options.getPointCloudCrsAuthoring;
    this.onFeaturesChanged = options.onFeaturesChanged;
    this.onFeaturePersisted = options.onFeaturePersisted;
    this.onFeaturesRemoved = options.onFeaturesRemoved;
  }

  public start(source: LineEditingActionSource): void {
    this.stop();
    const generation = this.generation;
    this.unsubscribe = source((action) => {
      void this.handleAction(action, generation);
    });
    this.logger.info('Controller started');
  }

  public stop(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.generation += 1;
  }

  private async handleAction(
    action: LineEditingAction,
    generation: number
  ): Promise<void> {
    if (this.generation !== generation) {
      return;
    }

    const scope = this.getScope();
    if (!scope) {
      this.logger.warn('Ignoring action; no project scope');
      return;
    }

    const featureService = this.getFeatureService();

    // Only bust the CDN after a write actually landed.
    let mutated = false;

    const removedIds = action.removedObjects
      .map((object) => this.readFeatureId(object))
      .filter((id): id is string => id !== undefined);
    if (removedIds.length > 0) {
      this.logger.info('Persisting deletions', { count: removedIds.length });
      try {
        await featureService.deleteFeatures(scope, removedIds);
        if (this.generation !== generation) {
          return;
        }
        this.onFeaturesRemoved?.(removedIds);
        mutated = true;
      } catch (error) {
        this.logger.error(
          'Failed to delete features',
          { removedIds },
          this.asError(error)
        );
      }
    }

    for (const object of action.upsertedObjects) {
      if (this.generation !== generation) {
        return;
      }
      if (
        await this.persistUpserted(scope, object, featureService, generation)
      ) {
        mutated = true;
      }
    }

    if (this.generation !== generation) {
      return;
    }
    if (mutated) {
      this.onFeaturesChanged?.();
    }
  }

  /** @returns true when the object was written to the backend successfully. */
  private async persistUpserted(
    scope: FeatureScope,
    object: ICADObject,
    featureService: IFeatureService,
    generation: number
  ): Promise<boolean> {
    if (!(object instanceof CADPoint) && !(object instanceof CompoundCurve)) {
      return false;
    }
    if (
      object instanceof CompoundCurve &&
      object.getPoints().length < MIN_LINESTRING_VERTICES
    ) {
      this.logger.warn('Cannot persist curve; fewer than two vertices', {
        guid: object.guid,
      });
      return false;
    }

    const activeLayer = this.getActiveLayer();
    const existingId = this.readFeatureId(object);
    const layerId = this.resolveLayerId(object, activeLayer);
    if (!layerId) {
      this.logger.warn('Cannot persist feature; no layer selected', {
        guid: object.guid,
      });
      return false;
    }

    const feature = this.buildFeature(object, layerId, existingId, activeLayer);

    try {
      let persistedId = existingId;
      let persistedFeature = feature;
      if (existingId) {
        this.logger.info('Persisting move', {
          id: existingId,
          type: feature.geometry.type,
        });
        await featureService.updateFeature(scope, feature);
      } else {
        this.logger.info('Persisting create', {
          guid: object.guid,
          type: feature.geometry.type,
        });
        const created = await featureService.createFeature(scope, feature);
        persistedId = created.id ?? existingId;
        persistedFeature = created.id
          ? { ...feature, id: created.id }
          : feature;
      }
      if (this.generation !== generation) {
        return false;
      }
      this.syncSourceFeature(object, persistedFeature);
      this.emitPersisted(object, feature, persistedId);
      return true;
    } catch (error) {
      this.logger.error(
        'Failed to persist feature',
        { guid: object.guid },
        this.asError(error)
      );
      return false;
    }
  }

  /**
   * Emits the persisted feature in WGS-84 (when a converter is available) so the
   * host can share it across panes. Falls back to the authored geometry when no
   * converter exists (degenerate un-georeferenced case).
   */
  private emitPersisted(
    object: CADPoint | CompoundCurve,
    feature: GeospatialFeature,
    persistedId: string | undefined
  ): void {
    if (!this.onFeaturePersisted) {
      return;
    }
    const wgs = this.wgsGeometry(object);
    const emitted: GeospatialFeature = wgs
      ? { ...feature, id: persistedId, geometry: wgs, crsUrn: undefined }
      : { ...feature, id: persistedId };
    this.onFeaturePersisted(emitted);
  }

  private buildFeature(
    object: CADPoint | CompoundCurve,
    layerId: string,
    existingId: string | undefined,
    activeLayer: MapLayer | undefined
  ): GeospatialFeature {
    const kind = cadGeometryKind(object);
    const { coordinates, crsUrn } = this.authorVertices(cadVertices(object));

    return {
      id: existingId,
      layerId,
      geometry: cadGeometryFromCoordinates(kind, coordinates),
      properties: this.buildProperties(object, existingId),
      metadata: this.buildMetadata(layerId, activeLayer, kind),
      crsUrn,
    };
  }

  /**
   * The features API merge-patches properties per key, so a move sends none:
   * CAD metadata is a create-time snapshot and would overwrite panel edits.
   */
  private buildProperties(
    object: CADPoint | CompoundCurve,
    existingId: string | undefined
  ): Record<string, unknown> {
    return existingId
      ? {}
      : { [DESCRIPTION_PROPERTY_KEY]: this.readDescription(object) };
  }

  /**
   * Authoring frame precedence (all-or-nothing per feature):
   * 1. validated point-cloud CRS; 2. WGS-84; 3. raw scene + CRS URN.
   */
  private authorVertices(vertices: readonly CadVertex[]): {
    coordinates: Position3D[];
    crsUrn: string | undefined;
  } {
    const pointCloudAuthoring = this.getPointCloudCrsAuthoring?.();
    if (pointCloudAuthoring) {
      const mapped = vertices.map((v) =>
        pointCloudAuthoring.toCoordinates(v.x, v.y, v.z)
      );
      if (mapped.every((c): c is Position3D => c !== undefined)) {
        return { coordinates: mapped, crsUrn: pointCloudAuthoring.crsUrn };
      }
    }

    const sceneToGeographic = this.getSceneToGeographic?.();
    if (sceneToGeographic) {
      const mapped = vertices.map((v): Position3D | undefined => {
        const geographic = sceneToGeographic(v.x, v.y);
        return geographic ? [geographic.lon, geographic.lat, v.z] : undefined;
      });
      if (mapped.every((c): c is Position3D => c !== undefined)) {
        return { coordinates: mapped, crsUrn: undefined };
      }
    }

    return {
      coordinates: vertices.map((v): Position3D => [v.x, v.y, v.z]),
      crsUrn: this.getCrsUrn(),
    };
  }

  private buildMetadata(
    layerId: string,
    activeLayer: MapLayer | undefined,
    geometryType: FeatureGeometry['type']
  ): Record<string, unknown> {
    const metadata: Record<string, unknown> = {
      [LAYER_ID_METADATA_KEY]: layerId,
      [GEOMETRY_TYPE_METADATA_KEY]: geometryType,
    };
    if (activeLayer?.templateId) {
      metadata[TEMPLATE_ID_METADATA_KEY] = activeLayer.templateId;
    }
    if (activeLayer?.templateSeriesId) {
      metadata[TEMPLATE_SERIES_ID_METADATA_KEY] = activeLayer.templateSeriesId;
    }
    return metadata;
  }

  private resolveLayerId(
    object: ICADObject,
    activeLayer: MapLayer | undefined
  ): string | undefined {
    const fromMeta = object.metaData[LAYER_ID_METADATA_KEY];
    if (typeof fromMeta === 'string' && fromMeta.length > 0) {
      return fromMeta;
    }
    return activeLayer?.id;
  }

  private readFeatureId(object: ICADObject): string | undefined {
    const id = object.metaData[CAD_FEATURE_ID_KEY];
    return typeof id === 'string' && id.length > 0 ? id : undefined;
  }

  /** Optimistic update / seed of the CAD object's linked source-feature cache. */
  private syncSourceFeature(
    object: CADPoint | CompoundCurve,
    persisted: GeospatialFeature
  ): void {
    // Prefer WGS-84 for the cache; skip geometry rewrite when conversion fails.
    const wgsGeometry = this.wgsGeometry(object);

    const existing = this.readSourceFeature(object);
    if (existing) {
      if (persisted.id) {
        existing.id = persisted.id;
        // Source may have been seeded before an id existed.
        stampFeatureLink(object.metaData, persisted.id, existing);
      }
      existing.properties = { ...existing.properties, ...persisted.properties };
      if (persisted.metadata) {
        existing.metadata = {
          ...(existing.metadata ?? {}),
          ...persisted.metadata,
        };
      }
      if (wgsGeometry) {
        existing.geometry = wgsGeometry;
      }
      return;
    }

    const seeded: CachedSourceFeature = {
      id: persisted.id,
      geometry: wgsGeometry ?? this.sceneGeometry(object),
      properties: { ...persisted.properties },
      metadata: { ...persisted.metadata },
    };
    if (persisted.id) {
      stampFeatureLink(object.metaData, persisted.id, seeded);
    } else {
      object.metaData[CAD_SOURCE_FEATURE_KEY] = seeded;
    }
  }

  /** WGS-84 {@link FeatureGeometry} for the object, or undefined when no converter. */
  private wgsGeometry(
    object: CADPoint | CompoundCurve
  ): FeatureGeometry | undefined {
    const sceneToGeographic = this.getSceneToGeographic?.();
    if (!sceneToGeographic) {
      return undefined;
    }
    const mapped = cadVertices(object).map((v): Position3D | undefined => {
      const geographic = sceneToGeographic(v.x, v.y);
      return geographic ? [geographic.lon, geographic.lat, v.z] : undefined;
    });
    if (!mapped.every((c): c is Position3D => c !== undefined)) {
      return undefined;
    }
    return cadGeometryFromCoordinates(cadGeometryKind(object), mapped);
  }

  private sceneGeometry(
    object: CADPoint | CompoundCurve
  ): CachedSourceFeature['geometry'] {
    const coordinates = cadVertices(object).map(
      (v): Position3D => [v.x, v.y, v.z]
    );
    return cadGeometryFromCoordinates(cadGeometryKind(object), coordinates);
  }

  private readSourceFeature(
    object: ICADObject
  ): CachedSourceFeature | undefined {
    const source = object.metaData[CAD_SOURCE_FEATURE_KEY];
    if (
      typeof source === 'object' &&
      source !== null &&
      'geometry' in source &&
      'properties' in source
    ) {
      return source as CachedSourceFeature;
    }
    return undefined;
  }

  private readDescription(object: ICADObject): string {
    const text = object.metaData[DESCRIPTION_PROPERTY_KEY];
    return typeof text === 'string' ? text : '';
  }

  private asError(error: unknown): Error {
    return error instanceof Error ? error : new Error(String(error));
  }
}
